import { ReactElement, forwardRef, useState } from 'react';
import { useEffect } from 'react';

import cn from 'classnames';
import { NavLink as RouterNavLink, useLocation } from 'react-router-dom';
import { useMatch } from 'react-router-dom';
import { t } from 'tools/i18n';

import { MY_EVENTS_ROUTE } from '../../routes/events/list';
import Icon, { TIconsLiteral } from '../icon';
import Typography, { TypographyTypes } from '../typography';
import s from './NavLink.module.scss';

export interface NavLinkProps {
  label: string | ReactElement;
  to: string;
  icon?: TIconsLiteral;
  typographyType?: TypographyTypes;
  eventCounter?: number;
  typographyClassName?: string;
  className?: string;
  weight?: 500 | 600 | 700 | undefined;
}

const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>((props, ref) => {
  const [active, setActive] = useState(false);
  const {
    label,
    to = '',
    icon,
    className = '',
    typographyType = 'h4',
    typographyClassName = '',
    eventCounter = 0,
    weight = 500,
  } = props;

  // Use `useMatch` to determine if the current path matches `to`
  const match = useMatch(to);
  const location = useLocation();

  // Update `active` based on the match
  useEffect(() => {
    setActive(!!match); // Convert match to boolean
  }, [match]);

  return (
    <RouterNavLink
      ref={ref}
      className={({ isActive }) =>
        cn(s.link, {
          [className]: className,
          [s.active]:
            isActive ||
            ((location.pathname.startsWith('/bank-details/') ||
              location.pathname.startsWith('/promoter-setting/')) &&
              label === t('common_organiser_details')),
        })
      }
      to={to}
      onClick={(e) => {
        const isActive =
          location.pathname === to ||
          (location.pathname.startsWith('/tickets-orders/') &&
            label === t('common_tickets_orders')) ||
          ((location.pathname.startsWith('/bank-details/') ||
            location.pathname.startsWith('/promoter-setting/')) &&
            label === t('common_organiser_details'));

        if (isActive) {
          e.preventDefault(); // stop navigation
        }
      }}>
      <>
        {icon && <Icon name={icon} className={s.icon} size={20} />}
        <Typography type={typographyType} className={typographyClassName} weight={weight}>
          {label}{' '}
          {to === MY_EVENTS_ROUTE && eventCounter > 0 && (
            <span className={cn(s.counter, { [s.active]: active })}>{eventCounter}</span>
          )}
        </Typography>
      </>
    </RouterNavLink>
  );
});

export default NavLink;
